import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash2,
  FileText,
  Image,
  Link2,
  Loader2,
  X,
  ArrowLeft,
  Upload,
  Sparkles
} from 'lucide-react';

const WATemplatesPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [activeVersion, setActiveVersion] = useState(0);
  const [generating, setGenerating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    media_url: '',
    media_type: 'none',
    show_media_url: false,
    versions: []
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/wa/templates');
      setTemplates(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTemplate(null);
    setActiveVersion(0);
    setFormData({
      name: '',
      content: '',
      media_url: '',
      media_type: 'none',
      show_media_url: false,
      versions: []
    });
    setShowModal(true);
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setActiveVersion(0);
    setFormData({
      name: template.name,
      content: template.content,
      media_url: template.media_url || '',
      media_type: template.media_type || 'none',
      show_media_url: template.show_media_url || false,
      versions: template.versions || []
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingTemplate) {
        await api.put(`/wa/templates/${editingTemplate.id}`, formData);
      } else {
        await api.post('/wa/templates', formData);
      }
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      alert('Error saving template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/wa/templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      alert('Error deleting template');
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('content-textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const before = text.substring(0, start);
    const after = text.substring(end);
    setFormData({
      ...formData,
      content: before + `{{${variable}}}` + after
    });
  };

  const addVersion = () => {
    const newVersions = [...formData.versions, { id: `v-${Date.now()}`, content: '' }];
    setFormData({ ...formData, versions: newVersions });
    setActiveVersion(newVersions.length); // Switch to the new version
  };

  const updateVersion = (index, content) => {
    const updated = [...formData.versions];
    updated[index] = { ...updated[index], content };
    setFormData({ ...formData, versions: updated });
  };

  const removeVersion = (index) => {
    const updated = formData.versions.filter((_, i) => i !== index);
    setFormData({ ...formData, versions: updated });
    // Adjust active version if needed
    if (activeVersion > updated.length) {
      setActiveVersion(updated.length > 0 ? updated.length : 0);
    } else if (activeVersion === index + 1) {
      setActiveVersion(0);
    }
  };

  // Get the content for the currently active version
  const getActiveContent = () => {
    if (activeVersion === 0) return formData.content;
    return formData.versions[activeVersion - 1]?.content || '';
  };

  const setActiveContent = (content) => {
    if (activeVersion === 0) {
      setFormData({ ...formData, content });
    } else {
      updateVersion(activeVersion - 1, content);
    }
  };

  const generateVersion = async () => {
    if (!formData.content) {
      alert('Write a primary message (Version 1) first before generating.');
      return;
    }
    setGenerating(true);
    try {
      const response = await api.post('/wa/templates/generate-version', { content: formData.content });
      if (response.data.success) {
        setActiveContent(response.data.content);
      }
    } catch (error) {
      alert('Failed to generate. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload JPG, PNG, GIF, or WebP images.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await api.post('/wa/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Construct full URL for the uploaded image
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = `${backendUrl}${response.data.url}`;
      
      setFormData({
        ...formData,
        media_url: fullUrl,
        media_type: 'image'
      });
    } catch (error) {
      alert('Error uploading file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const renderPreview = () => {
    let preview = formData.content;
    preview = preview.replace(/\{\{name\}\}/g, 'John Doe');
    preview = preview.replace(/\{\{phone\}\}/g, '+60123456789');
    return preview;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/whatsapp')}
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Message Templates</h1>
            <p className="text-gray-500 mt-1">Create and manage your message templates</p>
          </div>
        </div>
        <Button 
          onClick={openCreateModal}
          className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
        >
          <Plus size={16} className="mr-2" />
          Create Template
        </Button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.length > 0 ? templates.map((template) => (
          <Card key={template.id} className="p-6 bg-white border border-gray-100">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#4F46E5]/10 flex items-center justify-center">
                  <FileText className="text-[#4F46E5]" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500">
                    {template.versions && template.versions.length > 0 && (
                      <span className="inline-flex items-center gap-1 mr-2 text-[#4F46E5]">
                        {template.versions.length + 1} Versions
                      </span>
                    )}
                    {template.media_type !== 'none' && (
                      <span className="inline-flex items-center gap-1">
                        <Image size={12} /> Has media
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button 
                  onClick={() => openEditModal(template)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Pencil size={16} className="text-gray-500" />
                </button>
                <button 
                  onClick={() => handleDelete(template.id)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 size={16} className="text-red-500" />
                </button>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 max-h-32 overflow-y-auto whitespace-pre-wrap">
              {template.content.substring(0, 200)}
              {template.content.length > 200 && '...'}
            </div>
            
            {template.media_url && (
              <div className="mt-3">
                {/* Image Preview Thumbnail */}
                {!template.media_url.includes('youtube') && !template.media_url.includes('youtu.be') && (
                  <div 
                    className="mb-2 cursor-pointer"
                    onClick={() => setImagePreviewUrl(template.media_url)}
                  >
                    <img 
                      src={template.media_url} 
                      alt="Media preview" 
                      className="max-h-24 rounded-lg shadow-sm hover:opacity-90 transition-opacity"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                {/* Media URL Link */}
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Link2 size={14} />
                  <a href={template.media_url} target="_blank" rel="noopener noreferrer" className="truncate">
                    {template.media_url}
                  </a>
                </div>
              </div>
            )}
            
            <p className="mt-3 text-xs text-gray-400">
              Created by {template.created_by} • {new Date(template.created_at).toLocaleDateString()}
            </p>
          </Card>
        )) : (
          <Card className="col-span-full p-12 bg-white border border-gray-100 text-center">
            <FileText className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="font-semibold text-gray-900 mb-2">No templates yet</h3>
            <p className="text-gray-500 mb-4">Create your first message template to get started</p>
            <Button onClick={openCreateModal}>
              <Plus size={16} className="mr-2" />
              Create Template
            </Button>
          </Card>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Welcome Message"
                required
              />
            </div>
            
            {/* Split layout: Content editor + Version tabs */}
            <div className="flex gap-0 border border-gray-200 rounded-lg overflow-hidden" style={{ minHeight: '320px' }}>
              {/* Left: Message content editor */}
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">
                    {activeVersion === 0 ? 'Message Content' : `Version ${activeVersion + 1}`}
                    {activeVersion === 0 && <span className="text-red-500 ml-0.5">*</span>}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {activeVersion > 0 && (
                      <button
                        type="button"
                        onClick={generateVersion}
                        disabled={generating || !formData.content}
                        className="px-2 py-0.5 text-xs bg-[#4F46E5] text-white hover:bg-[#4338CA] disabled:opacity-50 rounded flex items-center gap-1"
                      >
                        {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                        {generating ? 'Generating...' : 'AI Generate'}
                      </button>
                    )}
                    <span className="text-xs text-gray-400">Insert:</span>
                    <button type="button" onClick={() => insertVariable('name')} className="px-1.5 py-0.5 text-xs bg-white border border-gray-200 hover:bg-gray-100 rounded">
                      {'{{name}}'}
                    </button>
                    <button type="button" onClick={() => insertVariable('phone')} className="px-1.5 py-0.5 text-xs bg-white border border-gray-200 hover:bg-gray-100 rounded">
                      {'{{phone}}'}
                    </button>
                    <button type="button" onClick={() => insertVariable('tracking')} className="px-1.5 py-0.5 text-xs bg-white border border-gray-200 hover:bg-gray-100 rounded">
                      {'{{tracking}}'}
                    </button>
                    <button type="button" onClick={() => insertVariable('order_id')} className="px-1.5 py-0.5 text-xs bg-white border border-gray-200 hover:bg-gray-100 rounded">
                      {'{{order_id}}'}
                    </button>
                  </div>
                </div>
                <Textarea
                  id="content-textarea"
                  value={getActiveContent()}
                  onChange={(e) => setActiveContent(e.target.value)}
                  placeholder={activeVersion === 0 ? "Hi {{name}}, thank you for your order..." : `Write version ${activeVersion + 1} of your message...`}
                  className="flex-1 border-0 rounded-none resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  style={{ minHeight: '270px' }}
                  required={activeVersion === 0}
                />
              </div>

              {/* Right: Version sidebar */}
              <div className="w-[160px] flex-shrink-0 bg-gray-50 border-l border-gray-200 flex flex-col">
                <div className="px-3 py-2 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Versions</span>
                </div>
                <div className="flex-1 flex flex-col">
                  {/* V1 - Primary */}
                  <button
                    type="button"
                    onClick={() => setActiveVersion(0)}
                    className={`px-3 py-2.5 text-left text-sm border-b border-gray-200 transition-colors ${
                      activeVersion === 0
                        ? 'bg-white text-[#4F46E5] font-semibold border-l-2 border-l-[#4F46E5]'
                        : 'text-gray-600 hover:bg-white'
                    }`}
                  >
                    Version 1 <span className="text-xs text-gray-400 ml-0.5">Primary</span>
                  </button>
                  
                  {/* Additional versions */}
                  {formData.versions.map((version, index) => (
                    <div key={version.id} className="relative group">
                      <button
                        type="button"
                        onClick={() => setActiveVersion(index + 1)}
                        className={`w-full px-3 py-2.5 text-left text-sm border-b border-gray-200 transition-colors ${
                          activeVersion === index + 1
                            ? 'bg-white text-[#4F46E5] font-semibold border-l-2 border-l-[#4F46E5]'
                            : 'text-gray-600 hover:bg-white'
                        }`}
                      >
                        Version {index + 2}
                        {!version.content && <span className="text-xs text-amber-500 ml-1">empty</span>}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeVersion(index); }}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  
                  {/* Add version button */}
                  <button
                    type="button"
                    onClick={addVersion}
                    className="px-3 py-2.5 text-left text-sm text-gray-400 hover:text-[#4F46E5] hover:bg-white border-b border-gray-200 transition-colors flex items-center gap-1"
                  >
                    <Plus size={12} /> Add
                  </button>
                </div>
                
                {formData.versions.length > 0 && (
                  <div className="px-2 py-2 border-t border-gray-200">
                    <p className="text-[10px] text-gray-400 leading-tight">Random version sent per client with equal probability</p>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Media (Optional)
              </label>
              
              {/* Upload Image */}
              <div className="flex items-center gap-3 mb-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2"
                >
                  {uploading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <Upload size={16} />
                  )}
                  {uploading ? 'Uploading...' : 'Upload Image'}
                </Button>
                <span className="text-xs text-gray-500">or</span>
              </div>
              
              {/* URL Input */}
              <Input
                value={formData.media_url}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  media_url: e.target.value,
                  media_type: e.target.value ? 'image' : 'none'
                })}
                placeholder="Paste image URL or YouTube link..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload an image or paste a URL. YouTube links will be included at the end of your message.
              </p>
              
              {/* Media Preview */}
              {formData.media_url && (
                <div className="mt-2 flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  {formData.media_url.includes('youtube') || formData.media_url.includes('youtu.be') ? (
                    <Link2 size={16} className="text-red-500" />
                  ) : (
                    <img 
                      src={formData.media_url} 
                      alt="Preview" 
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <span className="text-xs text-gray-600 truncate flex-1">{formData.media_url}</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, media_url: '', media_type: 'none' })}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <X size={14} className="text-gray-500" />
                  </button>
                </div>
              )}
              
              {/* Show Media URL Checkbox */}
              {formData.media_url && (
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_media_url}
                    onChange={(e) => setFormData({ ...formData, show_media_url: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-600">Show image URL in message</span>
                </label>
              )}
            </div>
            
            {formData.content && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preview
                </label>
                <div className="bg-[#DCF8C6] rounded-lg p-4 text-sm">
                  {/* Image Preview */}
                  {formData.media_url && !formData.media_url.includes('youtube') && !formData.media_url.includes('youtu.be') && (
                    <div 
                      className="mb-3 cursor-pointer"
                      onClick={() => setImagePreviewUrl(formData.media_url)}
                    >
                      <img 
                        src={formData.media_url} 
                        alt="Media preview" 
                        className="max-h-40 rounded-lg shadow-sm hover:opacity-90 transition-opacity"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                  
                  {/* Message Content */}
                  <div className="whitespace-pre-wrap">{renderPreview()}</div>
                  
                  {/* Media URL (if enabled) */}
                  {formData.media_url && formData.show_media_url && (
                    <div className="mt-2 text-blue-600 text-xs break-all">
                      {formData.media_url}
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t mt-4 flex-shrink-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving || uploading}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              >
                {saving ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : null}
                {editingTemplate ? 'Save Changes' : 'Create Template'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Full-size Image Preview Modal */}
      {imagePreviewUrl && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setImagePreviewUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setImagePreviewUrl(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={imagePreviewUrl} 
            alt="Full size preview" 
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default WATemplatesPage;
